import { Component, Input, OnInit, ViewChild, AfterViewInit, NgZone } from '@angular/core';
import { AgGridToolsService } from 'src/app/common/ag-grid-table/services/ag-grid-tools.service';
import { AgGridTableComponent } from 'src/app/common/ag-grid-table/ag-grid-table.component';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import { LoggerService } from 'src/app/common/services/logger.service';

@Component({
  selector: 'app-ag-grid-table-view',
  templateUrl: './ag-grid-table-view.component.html',
  styleUrls: ['./ag-grid-table-view.component.sass']
})
export class AgGridTableViewComponent implements OnInit, AfterViewInit {
  @Input() fieldOptions: any;
  @Input() fieldKey: string;
  @Input() record: any;
  @Input() isMultiple: boolean = false;
  @Input() isPrize: boolean = false; 
  public columnSelection: string = '';
  public buildParams: any = {};
  public tableParams: any = {};
  public bulkUpdateColDefs: any[] = [];
  @Input() parentViewName: any;
  @Input() customHeight: string | null = null;
  title: string;
  showPopup: boolean = false;
  prizeCounts: { [key: string]: number } = {};
  filterCounter: number = 0;
  popupFilterCounter: number = 0;
  sortCounter: number = 0;
  popupSortCounter: number = 0;
  
  @ViewChild('mainTable') mainTable: AgGridTableComponent;
  @ViewChild('popupTable') popupTable: AgGridTableComponent;

  constructor(
    private agGridToolsService: AgGridToolsService,
    private httpClient: HttpClient,
    private messageService: MessageService,
    private spinnerService: SpinnerService,
    private ngZone: NgZone,
    private logger: LoggerService
  ) { }

  ngOnInit(): void {
    this.title = this.fieldOptions?.title || 'Default Table Title';
    this.initializeTable();
  
    if (this.isPrize && Array.isArray(this.record[this.fieldKey])) {
      const prizeData = this.record[this.fieldKey];
      this.prizeCounts = this.extractCount(prizeData);
      this.logger.log('PrizeCounts initialized:', this.prizeCounts);
    }
  }
  
  ngAfterViewInit(): void {
    // Set up event listeners for filter changes
    if (this.mainTable) {
      this.setupFilterListeners();
    }
  }
  
  private setupFilterListeners(): void {
    // We need to access the ag-grid API to listen for filter changes
    // This will be done when the tables are ready
    setTimeout(() => {
      if (this.mainTable && this.mainTable.api) {
        this.mainTable.api.addEventListener('filterChanged', () => {
          this.updateMainFilterCounter();
        });
        this.mainTable.api.addEventListener('sortChanged', () => {
          this.updateMainSortCounter();
        });
      }
      
      if (this.popupTable && this.popupTable.api) {
        this.popupTable.api.addEventListener('filterChanged', () => {
          this.updatePopupFilterCounter();
        });
        this.popupTable.api.addEventListener('sortChanged', () => {
          this.updatePopupSortCounter();
        });
      }
    }, 1000); // Give the tables time to initialize
  }
  
  private updateMainFilterCounter(): void {
    if (this.mainTable && this.mainTable.api) {
      const filterModel = this.mainTable.api.getFilterModel();
      this.filterCounter = filterModel ? Object.keys(filterModel).length : 0;
    }
  }
  
  private updatePopupFilterCounter(): void {
    if (this.popupTable && this.popupTable.api) {
      const filterModel = this.popupTable.api.getFilterModel();
      this.popupFilterCounter = filterModel ? Object.keys(filterModel).length : 0;
    }
  }
  
  private updateMainSortCounter(): void {
    if (this.mainTable && this.mainTable.columnApi) {
      const columnState = this.mainTable.columnApi.getColumnState();
      const sortedColumns = columnState.filter((column: any) => column.sort !== null && column.colId !== 'id');
      this.sortCounter = sortedColumns ? sortedColumns.length : 0;
    }
  }
  
  private updatePopupSortCounter(): void {
    if (this.popupTable && this.popupTable.columnApi) {
      const columnState = this.popupTable.columnApi.getColumnState();
      const sortedColumns = columnState.filter((column: any) => column.sort !== null && column.colId !== 'id');
      this.popupSortCounter = sortedColumns ? sortedColumns.length : 0;
    }
  }
  
  private initializeTable(): void {
    this.columnSelection = this.agGridToolsService.getColumnFieldNamesFromCols(this.fieldOptions.columnDefs);
    this.buildParams = this.fieldOptions.buildParams || {};
    this.bulkUpdateColDefs = this.fieldOptions.bulkUpdateColDefs || [];
    this.tableParams = {
      defaultQuery: this.getDefaultQuery(),
      hideHeader: this.fieldOptions.hideHeader || true,
      isMultiple: this.isMultiple || false,
      entities: this.fieldOptions.entities || null,
      select: this.fieldOptions.select || null,
      isDynamicEntity: this.fieldOptions.isDynamicEntity || false,
    };
    this.logger.log('tableParams: ', this.tableParams);
  }
  private extractCount(records: any[]): { [key: string]: number } {
    if (!records) return {};
    
    const countDict: { [key: string]: number } = {};
    
    records.forEach((record: any) => {
      if (record?.id?._id && typeof record.c === 'number') {
        countDict[record.id._id] = record.c;
      }
    });
    
    return countDict;
  }
  
  private getDefaultQuery(): any {
    const defaultQueryKey = this.fieldOptions.defaultQueryKey;
    if (!defaultQueryKey || !this.record) {
      return null;
    }

    const mapQuery = (key: string) => {
      const data = this.record[key];
      if (!data) return [null];

      if (Array.isArray(data)) {
        return data.map((obj: any) => typeof obj === 'object' && obj._id ? obj._id : obj);
      }

      return [null];
    };

    const queryMappings: { [key: string]: () => any } = {
      'nurtures_ref': () => ({ _id: { $in: mapQuery('nurtures_ref') } }),
      'items_ref': () => ({ _id: { $in: mapQuery('items_ref') } }),
      'itemSet_ref': () => ({ itemSet_ref: this.record._id }),
      'seriesChallenges_ref': () => ({ _id: { $in: mapQuery('challenges_ref') } }),
      'seriesLoadingScreen_ref': () => ({ _id: { $in: mapQuery('loadingScreen_ref') } }),
      'seriesStoreListings_ref': () => {
        let ids = [
          ...mapQuery('storeListings_ref')
        ];
        return { _id: { $in: ids } };
      },
      'challengeCollection_ref': () => ({ _id: this.record.challengeCollection_ref?._id }),
      'seriesChallengeCollecions': () => {
        let ids:any[] = [];
        if(this.record.challengeCollection_ref && this.record.challengeCollection_ref?._id){
          ids.push(this.record.challengeCollection_ref._id)
        }
        if(this.record.secondaryChallengeCollection_ref && this.record.secondaryChallengeCollection_ref?._id){
          ids.push(this.record.secondaryChallengeCollection_ref._id)
        }
        return { _id: { $in: ids } }
      },
      'collections_ref': () => ({ _id: { $in: this.record.collections_ref || [] } }),
      'seriesInboxMessages_ref': () => {
        let ids: any[] = mapQuery('inboxMessages_ref'); 
        if (this.record.announcementInbox_ref?._id) {
          ids.push(this.record.announcementInbox_ref._id); 
        }
        return { _id: { $in: ids } };
      },
      'rewards_ref': () => {
        const rewardsData = this.record.rewards_ref || [];
        const ids = rewardsData.map((reward: any) => reward.id?._id).filter(Boolean);
        return { _id: { $in: ids } };
      },
      'itemsFromCollections': () => {
        const collections = this.record.collections_ref || []; 
        const itemIds = collections.flatMap((collection: any) => collection.items_ref || []); 
        return { _id: { $in: itemIds } }; 
      },
      'credits_ref': () => {
        const creditData = this.record.credits_ref || [];
        const ids = creditData.map((credit: any) => credit.id?._id).filter(Boolean);
        return { _id: { $in: ids } };
      },
      'byob_items': () => {
        this.logger.log(this.record);
        // item collections to Include
        const itemCollectionsToInclude = this.record.itemCollections_ref?.map((collection: any) => collection?.id).filter(Boolean) || [];
        // individual item Object Ids to include
        const itemOIdsToInclude = this.record.includeItem_refs?.map((item: any) => item?._id).filter(Boolean) || [];
        // item Object Ids to exclude
        const itemOIdsToExclude = this.record.excludeItem_refs?.map((item: any) => item?._id).filter(Boolean) || [];
        // item Collection Ids to exclude
        const itemCollectionIdsToExclude = this.record.excludeItemCollections_ref?.map((collection: any) => collection?.id).filter(Boolean) || [];
        
        this.logger.log('itemCollectionsToInclude: ', itemCollectionsToInclude);
        this.logger.log('itemOIdsToInclude: ', itemOIdsToInclude);
        this.logger.log('itemOIdsToExclude: ', itemOIdsToExclude);
        this.logger.log('itemCollectionIdsToExclude: ', itemCollectionIdsToExclude);
        
        // Build the query
        return {
          $and: [
            // Items must either be in includeItem_refs OR have collection in itemCollectionsToInclude
            {
              $or: [
                // Include specific items from includeItem_refs
                { _id: { $in: itemOIdsToInclude } },
                // Include items from collections
                { 'collection_ref.id': { $in: itemCollectionsToInclude } }
              ]
            },
            // Items must not have _id in itemOIdsToExclude
            { _id: { $nin: itemOIdsToExclude } },
            // Items must not have collection in itemCollectionIdsToExclude
            { 'collection_ref.id': { $nin: itemCollectionIdsToExclude } }
          ]
        };
      }
    };
    
    let query = queryMappings[defaultQueryKey]?.() || { _id: { $in: mapQuery(defaultQueryKey) } };
    return query;
  }    

  transformData(inputData: any) {
    const identity: any = {};

    inputData.forEach((item: any) => {
      let type;
    
      if (item.t.name === 'Currency') {
        type = 'currencies';
      } else if (item.t.name === 'SeedPackBox') {
        type = 'seed-pack-box';
      } else if (item.t.name === 'DynamicMysteryBox') {
        type = 'dynamic-mystery-box';
      } else {
        type = item.t.name.toLowerCase() + 's'; // Default lowercase plural form
      }
    
      const id = item.id.id; // Extract id
    
      if (!identity[type]) {
        identity[type] = [];
      }
    
      identity[type].push(id);
    });
    
    this.logger.log(identity);
    return identity;
    
  }

  openPopup(): void {
    this.showPopup = true;
    // Set up filter listeners for the popup table when it's opened
    setTimeout(() => {
      this.setupFilterListeners();
    }, 500);
  }

  closePopup(): void {
    this.showPopup = false;
  }

  showMainTableActions(): void {
    this.mainTable?.showActionsPanel();
  }

  showPopupTableActions(): void {
    this.popupTable?.showActionsPanel();
  }
  
  clearFilters(): void {
    // This method will be called when the clear filters button in the main view is clicked
    if (this.mainTable) {
      this.logger.log('Clearing filters for the main table');
      this.mainTable.clearFilters();
      // Update the filter counter
      this.filterCounter = 0;
    }
  }

  
  clearPopupFilters(): void {
    // This method will be called when the clear filters button in the popup is clicked
    if (this.popupTable) {
      this.logger.log('Clearing filters for the popup table');
      this.popupTable.clearFilters();
      // Update the filter counter
      this.popupFilterCounter = 0;
    }
  }
  
  clearSorting(): void {
    // This method will be called when the clear sorting button in the main view is clicked
    if (this.mainTable) {
      this.logger.log('Clearing sorting for the main table');
      this.mainTable.clearSorting();
      // Update the sort counter
      this.sortCounter = 0;
    }
  }
  
  clearPopupSorting(): void {
    // This method will be called when the clear sorting button in the popup is clicked
    if (this.popupTable) {
      this.logger.log('Clearing sorting for the popup table');
      this.popupTable.clearSorting();
      // Update the sort counter
      this.popupSortCounter = 0;
    }
  }

  exportToCSV(): void {
    // Export CSV for the main table
    if (this.mainTable) {
      this.performExport(this.mainTable);
    }
  }

  exportPopupToCSV(): void {
    // Export CSV for the popup table
    if (this.popupTable) {
      this.performExport(this.popupTable);
    }
  }

  private performExport(tableComponent: AgGridTableComponent): void {
    this.ngZone.runOutsideAngular(() => {
      this.spinnerService.loadSpinner();
    });

    const api = tableComponent.api;
    const columnApi = tableComponent.columnApi;
    const entity = this.fieldOptions?.entity;

    if (!api || !columnApi || !entity) {
      this.spinnerService.endSpinner();
      this.messageService.add({
        severity: 'error',
        summary: 'Export Error',
        detail: 'Unable to export CSV. Table not properly initialized.'
      });
      return;
    }

    // Get currently displayed columns
    const currentDisplayedColumns = columnApi.getAllDisplayedColumns();
    const columnsToExport = currentDisplayedColumns
      .map((col: any) => ({
        colId: col.getColId(),
        colName: col.getUserProvidedColDef().headerName
      }))
      .filter((colObj: any) => colObj.colId !== 'actions');

    // Get the current filter model from the table
    const filterModel = api.getFilterModel();
    
    // Get sort information from column state
    const columnState = columnApi.getColumnState();
    const sortedColumns = columnState.filter((column: any) => column.sort !== null);
    
    // Build MongoDB query from table's current query
    let mongoQuery = this.tableParams?.defaultQuery || {};
    
    // If there's a custom query from tableParams, use it
    if (this.tableParams?.query) {
      mongoQuery = { ...mongoQuery, ...this.tableParams.query };
    }

    // Build MongoDB sort from column state
    const mongoSort: any = {};
    if (sortedColumns && sortedColumns.length > 0) {
      sortedColumns.forEach((column: any) => {
        mongoSort[column.colId] = column.sort === 'asc' ? 1 : -1;
      });
    }

    // Construct the request payload
    const payload = {
      query: mongoQuery,
      sort: mongoSort,
      columns: columnsToExport
    };

    // Determine the correct endpoint based on whether this is a dynamic entity
    // Check tableParams first, then fieldOptions as fallback
    const isDynamicEntity = this.tableParams?.isDynamicEntity || this.fieldOptions?.isDynamicEntity || false;
    const csvEndpoint = isDynamicEntity 
      ? `api/dynamic-entities/manager/${entity}/csv`
      : `api/${entity}/csv`;

    // Make an HTTP POST request
    this.httpClient.post(csvEndpoint, payload, { responseType: 'text' })
      .subscribe({
        next: (csv) => {
          const currentDate = new Date();
          const formattedDate = currentDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
          }).replace(/\//g, '-');

          // Construct the file name
          const fileName = `${entity}-${formattedDate}.csv`;

          // Trigger file download
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
          this.spinnerService.endSpinner();
        },
        error: (error) => {
          this.spinnerService.endSpinner();

          if (error.status === 403) {
            let errorMessage = error?.error?.error;

            if (typeof error?.error === 'string') {
              try {
                const errorJson = JSON.parse(error?.error);
                errorMessage = errorJson.error;
              } catch (_) {}
            }

            this.messageService.add({
              sticky: false,
              severity: 'error',
              summary: `ERROR exporting`,
              detail: `You don't have permissions to export ${entity}. Error: ${errorMessage}`
            });
          } else if (error.status === 404) {
            this.messageService.add({
              sticky: false,
              severity: 'error',
              summary: `ERROR exporting`,
              detail: `Export endpoint not found for ${entity}`
            });
          } else {
            this.messageService.add({
              sticky: false,
              severity: 'error',
              summary: `ERROR exporting`,
              detail: `Failed to export ${entity} to CSV`
            });
          }
        }
      });
  }
}
